import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ClientTestimonial from '../components/ClientTestimonial';
import SEO from '../components/SEO';
import { DiagnosticsInstruments } from '../components/DiagnosticsInstruments';
import { ImpactFilmInline, filmJsonLd } from '../components/ImpactFilm';
import { ArrowRight, User, Users, Building, CheckCircle, FileText } from 'lucide-react';

const impactASPlusLogo = "/opt/brand/impactas-final-2.webp";

const whyItExists = [
  "Where are our real leadership strengths and risks?",
  "How do individual behaviours aggregate into team and organisational outcomes?",
  "Which issues are structural, and which are behavioural?",
  "Where should we intervene now, and where should we wait?"
];

const whatItEnables = [
  "Prioritise leadership development with clarity",
  "Identify behavioural and execution risks early",
  "Ground coaching and development in evidence",
  "Align leadership behaviour with strategy and culture",
  "Move beyond intuition to insight-led action"
];

const typicalUses = [
  "Organisation-wide diagnostics or 360° programmes",
  "Leadership cohort or succession insight",
  "Hiring, from graduate intakes to senior appointments (JobFit™, JobFit Plus™ and InsightEdge™)",
  "Targeted coaching informed by diagnostic data",
  "Culture, ethics, or behavioural risk reviews",
  "Pilots and phased rollouts before scaling"
];

const ImpactASPlusPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Leadership Assessment Platform Nigeria | Impact AS Plus Lagos"
        description="Impact AS Plus™: twelve leadership instruments for Nigerian organisations, assessment, 360, culture and hiring from graduate intakes to senior appointments."
        keywords="leadership assessment platform Nigeria, talent intelligence Lagos, HR technology Nigeria, leadership diagnostics platform Africa, assessment software Nigeria, talent management system Lagos, leadership analytics Nigeria, HR tech Africa"
        canonical="/impact-as-plus"
        jsonLd={filmJsonLd}
      />
      <Header />
      
      <main>
        {/* Hero Section with Background Image */}
        <section 
          className="relative min-h-[70vh] lg:min-h-[75vh] flex items-center bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(10, 10, 10, 0.95) 0%, rgba(10, 10, 10, 0.8) 40%, rgba(10, 10, 10, 0.4) 100%), url('/opt/photo/image-2.webp')`
          }}
        >
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-24">
            <div className="max-w-3xl">
              {/* Logo */}
              <img 
                src={impactASPlusLogo} 
                alt="Impact AS Plus" 
                className="h-14 lg:h-16 w-auto mb-10"
              />
              
              <p className="text-[#2693d0] text-lg lg:text-xl xl:text-2xl font-medium leading-relaxed mb-6">
                RESILIGENT's proprietary leadership and organisational intelligence system
              </p>
              
              <p className="text-slate-300 text-base lg:text-lg leading-relaxed mb-6">
                At the core of our work is Impact AS Plus™, RESILIGENT's proprietary system for diagnosing leadership capability, behavioural dynamics, and organisational health in a coherent, evidence-based way.
              </p>
              
              <p className="text-slate-300 text-base lg:text-lg leading-relaxed mb-6">
                Impact AS Plus™ connects assessments, development and progress in one record across the leader, the team and the organisation, so the decisions you make about people rest on evidence you can explain and defend.
              </p>
              
              <p className="text-slate-300 text-base lg:text-lg xl:text-xl leading-relaxed max-w-2xl mb-10">
                Impact AS Plus enables organisations to see leadership and culture clearly, connect insight across levels, and make confident decisions about where to focus development, intervention, and investment.
              </p>
              
              <a
                href="/impact-as-plus-portal-main"
                className="inline-flex items-center gap-2 bg-[#2693d0] text-white px-6 py-3.5 rounded font-medium text-base hover:bg-[#1e7ab8] transition-all duration-300"
              >
                Explore Impact AS Plus™
                <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </section>

        {/* Why Impact AS Plus Exists */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  Why Impact AS Plus Exists
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                  Many organisations invest heavily in leadership development, coaching, and culture initiatives, yet struggle to answer basic questions with confidence:
                </p>
                <ul className="space-y-4 mb-8">
                  {whyItExists.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="lg:pt-16">
                <ImpactFilmInline />
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                  These challenges often arise because insight is fragmented, spread across disconnected assessments, anecdotal feedback, and intuition-led judgement.
                </p>
                <div className="bg-[#f0f5f8] rounded-xl p-6 lg:p-8 border border-slate-100">
                  <p className="text-slate-800 font-medium text-lg lg:text-xl leading-relaxed">
                    Impact AS Plus was designed to solve this problem.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* One System. Three Levels of Intelligence */}
        <section className="bg-[#1e3a5f] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-white font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                One System. Three Levels of Intelligence.
              </h2>
              <p className="text-slate-300 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto">
                Impact AS Plus integrates insight across three interconnected levels, providing a unified view rather than isolated data points.
              </p>
            </div>

            {/* Three Levels Visual */}
            <div className="max-w-4xl mx-auto">
              {/* Organisational Level - Outer */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 lg:p-10">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#2693d0]/20 flex items-center justify-center">
                    <Building className="w-6 h-6 text-[#2693d0]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-xl">Organisational Intelligence™</h3>
                  </div>
                </div>
                <p className="text-slate-300 text-base leading-relaxed mb-8 pl-16">
                  Surfacing cultural patterns, execution risk, alignment, and systemic enablers or blockers at an organisational level.
                </p>

                {/* Team Level - Middle */}
                <div className="bg-white/5 border border-white/15 rounded-xl p-6 lg:p-8 ml-4 lg:ml-8">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-[#2693d0]/25 flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#2693d0]" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg">Team Intelligence™</h3>
                    </div>
                  </div>
                  <p className="text-slate-300 text-base leading-relaxed mb-6 pl-14">
                    Making visible how leadership styles, decision patterns, and behavioural dynamics interact within teams, committees, and leadership groups.
                  </p>

                  {/* Individual Level - Inner */}
                  <div className="bg-white/5 border border-white/20 rounded-lg p-5 lg:p-6 ml-4 lg:ml-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-[#2693d0]/30 flex items-center justify-center">
                        <User className="w-5 h-5 text-[#2693d0]" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold text-base">Individual Intelligence™</h3>
                      </div>
                    </div>
                    <p className="text-slate-300 text-sm leading-relaxed pl-12">
                      Understanding leadership capability, behaviour, preferences, and risk at the individual level, grounded in behavioural and psychometric insight.
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-slate-300 text-base lg:text-lg leading-relaxed text-center mt-10 max-w-2xl mx-auto">
                Together, these layers allow organisations to see how leadership behaviour scales, rather than treating individuals, teams, and culture as separate conversations.
              </p>
            </div>
            
            {/* Client Testimonial - Olufemi Bakare */}
            <div className="mt-16 max-w-3xl mx-auto">
              <ClientTestimonial
                quote="The combination of data analysis and human insight produced strategies that accelerated our growth while addressing our underlying organisational challenges in a sustainable way."
                name="Olufemi Bakare"
                title="CEO"
                company="Fenchurch Energy Group"
                variant="dark"
              />
            </div>
          </div>
        </section>

        {/* What Impact AS Plus Enables */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                What Impact AS Plus Enables
              </h2>
              
              <div className="space-y-5 mb-10">
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  Impact AS Plus is not designed to produce reports for their own sake.
                </p>
                <p className="text-slate-800 font-medium text-lg lg:text-xl leading-relaxed">
                  It is designed to support better leadership decisions.
                </p>
              </div>

              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                Organisations use Impact AS Plus to:
              </p>
              
              <ul className="space-y-4">
                {whatItEnables.map((item, index) => (
                  <li key={index} className="flex items-center gap-4">
                    <CheckCircle className="w-5 h-5 text-[#2693d0] flex-shrink-0" />
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Between sessions, every leader has a companion (LINC) */}
        <section className="bg-[#1e3a5f] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-white font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                Between sessions, every leader has a companion
              </h2>
              <p className="text-slate-300 text-base lg:text-lg leading-relaxed mb-8">
                LINC™, the leadership intelligence companion, holds what each leader has learned and helps them apply it to the decisions in front of them every day, with nudges, coaching and a sparring partner for the hard calls. What they agree to work on is kept, so progress can be seen and measured.
              </p>
              <a
                href="/product/linc"
                className="inline-flex items-center gap-2 text-[#2693d0] font-medium hover:underline"
                data-testid="how-linc-works-link"
              >
                How LINC works
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </section>

        {/* How Organisations Typically Use It */}
        <section className="relative bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  How Organisations Typically Use Impact AS Plus
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  Organisations engage with Impact AS Plus in different ways, depending on context and need. Common starting points include:
                </p>
              </div>
              
              <div>
                <ul className="space-y-4 mb-8">
                  {typicalUses.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  Most organisations begin with a focused use case and expand over time as insight accumulates and priorities evolve.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Relationship Section */}
        <section className="bg-white py-20 lg:py-28 border-t border-slate-100">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                The Relationship Between Impact AS Plus and RESILIGENT
              </h2>
              
              <div className="space-y-6">
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  Impact AS Plus underpins all RESILIGENT advisory, coaching, and leadership development work.
                </p>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  It does not replace judgement, experience, or leadership conversations. Instead, it strengthens them, providing a consistent, credible evidence base that supports sound decision-making, governance, and accountability.
                </p>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  RESILIGENT combines this system with deep contextual understanding, advisory capability, and delivery experience across Africa and beyond.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Proprietary Diagnostics */}
        <section className="relative bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                Proprietary Diagnostics Within Impact AS Plus
              </h2>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                Impact AS Plus powers a suite of proprietary diagnostics designed specifically for African organisational contexts.
              </p>
            </div>
            
            <DiagnosticsInstruments className="mb-10" />
            
            {/* Cross-link to Leadership & Succession Intelligence */}
            <div className="bg-white rounded-xl p-6 lg:p-8 card-pulse-border">
              <p className="text-slate-700 text-base lg:text-lg leading-relaxed mb-4">
                Impact AS Plus™ also powers our <span className="font-medium">Leadership & Succession Intelligence</span> capability, providing board-ready insight into leadership pipelines, readiness, and transition risk.
              </p>
              <a
                href="/leadership-succession-intelligence"
                className="inline-flex items-center gap-2 text-[#2693d0] font-medium hover:underline"
              >
                Explore Leadership & Succession Intelligence
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-[#e8f2f7] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                Explore Further
              </h2>
              
              {/* Primary CTA */}
              <div className="bg-white card-pulse-border rounded-xl p-8 mb-6 shadow-sm">
                <h3 className="text-slate-800 font-semibold text-xl mb-4">
                  Book a leadership briefing
                </h3>
                <p className="text-slate-600 text-base leading-relaxed mb-6">
                  A guided walkthrough of how Impact AS Plus can be applied in your organisational context, whether your focus is leadership development, 360 feedback, succession, coaching, or culture.
                </p>
                <a
                  href="/briefing"
                  className="inline-flex items-center gap-2 bg-[#2693d0] text-white px-6 py-3.5 rounded font-medium text-base hover:bg-[#1e7ab8] transition-all duration-300"
                  data-testid="book-briefing-btn"
                >
                  Book a leadership briefing
                  <ArrowRight size={18} />
                </a>
              </div>

              {/* Secondary CTA */}
              <a
                href="/sample-reports"
                className="inline-flex items-center gap-3 text-slate-600 hover:text-[#2693d0] transition-colors duration-300"
                data-testid="view-sample-reports-link"
              >
                <FileText className="w-5 h-5" />
                <span className="text-base font-medium">View sample reports</span>
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer hidePreFooterCta={true} />
    </div>
  );
};

export default ImpactASPlusPage;
